import { useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// usePolling — calls `fn` on an interval, with two important refinements:
//
// 1. Visibility-aware: pauses when the tab is hidden. Kitchen tablets
//    sometimes have a second tab open; if it's not visible we stop polling
//    so we don't churn the DB for a screen no one is looking at. When the
//    tab becomes visible again we fire `fn` immediately to catch up.
//
// 2. Re-entry guard: if `fn` is async and slower than `intervalMs`, we
//    don't pile up overlapping calls. We track in-flight state and skip
//    the next tick if the previous hasn't returned.
//
// `fn` is read through a ref so updates to its closure don't trigger a
// re-subscribe storm — the interval keeps running across re-renders.
// ─────────────────────────────────────────────────────────────────────────────

export function usePolling(
  fn: () => Promise<void> | void,
  intervalMs: number,
  enabled: boolean = true,
): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let inFlight = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (document.hidden) {
        // Skip while hidden; we'll wake on visibilitychange.
        scheduleNext();
        return;
      }
      if (inFlight) {
        // Previous call still running; skip this tick.
        scheduleNext();
        return;
      }
      inFlight = true;
      try {
        await fnRef.current();
      } catch {
        // Errors are the caller's concern (they own the store). We just
        // keep the timer alive so transient failures don't kill polling.
      } finally {
        inFlight = false;
      }
      scheduleNext();
    };

    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = setTimeout(tick, intervalMs);
    };

    const onVisibilityChange = () => {
      if (cancelled) return;
      if (!document.hidden) {
        // Tab just became visible — refresh now, don't wait for the next tick.
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        void tick();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // Initial fetch fires immediately; subsequent ticks are scheduled inside `tick`.
    void tick();

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, enabled]);
}
